import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import styled, { css } from 'styled-components';
import { ObjectFit, ObjectPosition } from '@westwing/ui-kit/Picture/Image';
import UIkitPicture, { ImageFormat, Source } from '@westwing/ui-kit/Picture';
import { useMemo } from 'react';
import AspectRatio from '../Layout/AspectRatio';
import { isSupportedImageType } from './useMediaType';
import { useAppShell } from '@ids/appshell';
import { IDSContentfulImageFile } from '@ids/types/contentful';
import { makeMediaSources } from '@ids/components/Media/makeMediaSources';

/**
 * We consider LANDSCAPE images those with the 3:2 format aspect ratio or higher
 *
 * Anything else below the 3:2 format will be considered a PORTRAIT image
 */
export type Orientation = 'LANDSCAPE' | 'PORTRAIT';

export const getImageOrientation = (dimensions: { width: number; height: number }): Orientation =>
    dimensions.width / dimensions.height >= 1.5 ? 'LANDSCAPE' : 'PORTRAIT';

const screenSizes = [768, 1024, 1200, 1440, 1920];

export const makeSourcesSet = (
    quality: number,
    payload?: IDSContentfulImageFile | [IDSContentfulImageFile, IDSContentfulImageFile] | string
): { webpSrcSet: string; jpegSrcSet: string; fallbackUrl: string } => {
    return screenSizes.reduce(
        (acc, screenSize, index) => {
            let imageSrc;

            if (Array.isArray(payload)) {
                imageSrc = (payload[index] ?? payload[payload.length - 1])?.file.url;
            } else if (typeof payload === 'string') {
                imageSrc = payload;
            } else if (typeof payload?.file.url === 'string') {
                imageSrc = payload.file.url;
            }

            const webpSrc = `${imageSrc}?w=${screenSize}&q=${quality}&fm=webp`;
            acc.webpSrcSet = `${acc.webpSrcSet}${acc.webpSrcSet ? ', ' : ''}${webpSrc} ${screenSize}w`;

            const jpegSrc = `${imageSrc}?w=${screenSize}&q=${quality}&fm=jpg`;
            acc.jpegSrcSet = `${acc.jpegSrcSet}${acc.jpegSrcSet ? ', ' : ''}${jpegSrc} ${screenSize}w`;
            if (!acc.fallbackUrl) {
                acc.fallbackUrl = `${imageSrc}?w=${screenSize}&q=${quality}&fm=jpg`;
            }

            return acc;
        },
        {
            webpSrcSet: '',
            jpegSrcSet: '',
            fallbackUrl: '',
        }
    );
};

const imageStyles = css`
    img {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        box-sizing: border-box;
        padding: 0;
        border: none;
        margin: auto;
        display: block;
        width: 0;
        height: 0;
        min-width: 100%;
        max-width: 100%;
        min-height: 100%;
        max-height: 100%;
    }
`;

export type StyledImageWrapperProps =
    | {
          width?: never;
          height?: never;
          layout?: 'fill';
      }
    | {
          width: number | string;
          height: number | string;
          layout?: 'responsive' | 'intrinsic';
      };

const StyledImageWrapper = styled.div<StyledImageWrapperProps>`
    --width: ${({ width }) => width};
    --height: ${({ height }) => height};

    ${({ layout }) => {
        if (layout === 'fill') {
            return css`
                ${imageStyles}

                display: block;
                overflow: hidden;
                position: absolute;
                top: 0;
                left: 0;
                bottom: 0;
                right: 0;
                box-sizing: border-box;
                margin: 0;

                img {
                    object-fit: cover;
                }
            `;
        }
        if (layout === 'responsive') {
            return css`
                ${imageStyles}

                display: block;
                overflow: hidden;
                position: relative;
                box-sizing: border-box;
                margin: 0;
                padding-top: calc(100% / (var(--width) / var(--height)));
            `;
        }

        return null;
    }}
`;

type MediaImageLayoutWrapperProps = StyledImageWrapperProps & {
    ratio?: number | string;
    maxWidth?: number | string;
};

const MediaImageLayoutWrapper = ({
    children,
    maxWidth,
    ...restProps
}: React.PropsWithChildren<MediaImageLayoutWrapperProps>) => {
    const wrapperProps = { layout: restProps.layout } as StyledImageWrapperProps;

    if (restProps.layout === 'responsive') {
        wrapperProps.width = restProps.width;
        wrapperProps.height = restProps.height;
    }

    const ratio =
        typeof restProps.width === 'number' && typeof restProps.height === 'number'
            ? restProps.width / restProps.height
            : 1;

    if (wrapperProps.layout === 'intrinsic') {
        return (
            <AspectRatio ratio={ratio} maxWidth={maxWidth}>
                <StyledImageWrapper {...wrapperProps}>{children}</StyledImageWrapper>
            </AspectRatio>
        );
    }
    return <StyledImageWrapper {...wrapperProps}>{children}</StyledImageWrapper>;
};

export type ImageProps = React.ImgHTMLAttributes<HTMLImageElement> &
    StyledImageWrapperProps & {
        /**
         * Accepts a boolean value.
         * If true it will preload the picture component.
         *
         * @default false
         */
        priority?: boolean;
        /**
         * Accepts a number value which alters the quality of the image
         *
         * @default 70
         */
        quality?: number;
        maxWidth?: number | string;
        objectFit?: ObjectFit;
        objectPosition?: ObjectPosition;
    };

/**
 * We can pass the src to the and the alt tag to the Image component.
 * For example on the product cells we only have the src information.
 *
 * @example
 * ```tsx
 *  <MediaImage
 *      src={imageUrl}
 *      alt={altTag}
 *      ...
 *  />
 * ```
 */
type SingleImageSrc = { src: string; alt: string; images?: never; image?: never };
/**
 * For a single image we just pass the Contentful image file.
 *
 * @example
 * ```tsx
 *  <Media.Image
 *      image={image}
 *      sizes="(max-width: 1440px) 100vw, 33vw"
 *      ...
 *  />
 * ```
 
 */
type SingleImage = { images?: never; image: IDSContentfulImageFile };
/**
 * For images on different views we can pass an array of Contentful image files
 * It currently supports passing two images, the first in the array will be used
 * as the mobile image and the second one as the desktop image.
 *
 * @example
 * ```tsx
 *  <MediaImage
 *      priority
 *      images={[heroBlock.images.mobile, heroBlock.images.desktop]}
 *      ...
 * />
 * ```
 */
type MultipleImages = { images: [IDSContentfulImageFile, IDSContentfulImageFile]; image?: never };

export type MediaImageProps = Omit<ImageProps, keyof MediaImageLayoutWrapperProps> & {
    'data-testid'?: string;
    useMediaQuerySources?: boolean;
} & (SingleImageSrc | SingleImage | MultipleImages);

type MediaImageWithMediaQuerySourcesProps = Omit<MediaImageProps, 'useMediaQuerySources' | 'images'> & {
    logImageErrorWarning: () => void;
    images: [IDSContentfulImageFile, IDSContentfulImageFile] | undefined;
};
const MediaImageWithMediaQuerySources = ({
    priority,
    quality = 70,
    image,
    images,
    objectFit,
    objectPosition,
    'data-testid': testId,
    logImageErrorWarning,
    ...imageProps
}: MediaImageWithMediaQuerySourcesProps) => {
    const { sources: sourcesMemo, webpSrcSets: webpSrcSetsMemo } = useMemo(() => {
        const sources = makeMediaSources(quality, images ?? image ?? imageProps.src);
        const webpSourcesGroupedByMedia = sources
            .filter(({ format }) => format === ImageFormat.WEBP)
            .reduce<{ [key: string]: Source[] }>(
                (groups, item) => ({
                    ...groups,
                    [item.media]: [...(groups[item.media] || []), item],
                }),
                {}
            );

        const webpSrcSets = Object.entries(webpSourcesGroupedByMedia).map(([media, webpSources]) => {
            const imageSrcSet = webpSources.map(({ url, value }) => `${url} ${value}x`).join(', ');
            return { media, imageSrcSet };
        });

        return { sources, webpSrcSets };
    }, [image, imageProps.src, images, quality]);

    return (
        <React.Fragment>
            {priority && (
                <Helmet>
                    {webpSrcSetsMemo.map(({ media, imageSrcSet }) => (
                        <link key={imageSrcSet} rel="preload" as="image" imageSrcSet={imageSrcSet} media={media} />
                    ))}
                </Helmet>
            )}
            <UIkitPicture
                testId={testId}
                objectFit={objectFit}
                objectPosition={objectPosition}
                sources={sourcesMemo}
                loading={priority ? 'eager' : 'lazy'}
                fallback={{
                    url: sourcesMemo[0].url,
                    className: imageProps.className,
                    alt: imageProps?.alt || (image ?? images?.[0])?.title,
                }}
                errorFunction={logImageErrorWarning}
                loadFunction={imageProps.onLoad}
            />
        </React.Fragment>
    );
};

// eslint-disable-next-line complexity
const MediaImage = ({
    priority,
    quality = 70,
    sizes = '100vw',
    useMediaQuerySources = false,
    image,
    images,
    objectFit,
    objectPosition,
    'data-testid': testId,
    ...imageProps
}: MediaImageProps) => {
    const { logger } = useAppShell();

    const [validSrc = ''] = images?.map(({ file }) => file.url) || [image?.file?.url || imageProps.src];

    if (!isSupportedImageType.test(validSrc)) {
        return null;
    }

    const logImageErrorWarning = () => {
        logger.warn(`[Contentful] Interior Design Service Image with src - "${validSrc}" failed to load`);
    };

    if (useMediaQuerySources) {
        return (
            <MediaImageWithMediaQuerySources
                priority={priority}
                quality={quality}
                image={image}
                images={images}
                objectFit={objectFit}
                objectPosition={objectPosition}
                logImageErrorWarning={logImageErrorWarning}
                {...{ 'data-testid': testId }}
                {...imageProps}
            />
        );
    }

    const { webpSrcSet, jpegSrcSet, fallbackUrl } = makeSourcesSet(quality, images ?? image ?? imageProps.src);

    return (
        <React.Fragment>
            {priority && (
                <Helmet>
                    {/* eslint-disable-next-line */}
                    {/* @ts-ignore */}
                    <link rel="preload" as="image" imageSrcSet={webpSrcSet} imageSizes={sizes} />
                </Helmet>
            )}
            <UIkitPicture
                testId={testId}
                objectFit={objectFit}
                objectPosition={objectPosition}
                sources={
                    <React.Fragment>
                        <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
                        <source type="image/jpeg" srcSet={jpegSrcSet} sizes={sizes} />
                    </React.Fragment>
                }
                loading={priority ? 'eager' : 'lazy'}
                fallback={{
                    url: fallbackUrl,
                    className: imageProps.className,
                    alt: imageProps?.alt || (image ?? images?.[0])?.title,
                }}
                errorFunction={logImageErrorWarning}
                loadFunction={imageProps.onLoad}
            />
        </React.Fragment>
    );
};

type MediaImageWithLayoutProps = MediaImageProps & MediaImageLayoutWrapperProps;

const MediaImageWithLayout = ({
    layout = 'fill',
    width,
    height,
    maxWidth,
    children = null,
    ...mediaImageProps
}: MediaImageWithLayoutProps) => (
    <MediaImageLayoutWrapper maxWidth={maxWidth} {...({ layout, width, height } as StyledImageWrapperProps)}>
        {children}
        <MediaImage {...mediaImageProps} />
    </MediaImageLayoutWrapper>
);

MediaImage.Full = MediaImageWithLayout;

export { MediaImageWithLayout as default, MediaImage };
